import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  Text,
  View,
  SectionList,
  Modal,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";
import {
  getDeliveriesList,
  markDelivery,
  markOrderAsNotDelivered,
} from "@services/api";
import DeliveryCard from "@components/CMS/DeliveryCard";
import { useDispatch } from "react-redux";
import BackArrow from "@assets/svg/BackArrow";
import CheckMultiLayer from "@assets/svg/CheckMultiLayer";
import CloseIcon from "@assets/svg/CloseIcon";
import { actions as orderActions } from "@reducers/orderReducer";
import NoOrdersGraphic from "@assets/svg/NoOrdersGraphic";
import t from "@constants/localization";

// Events
import analytics from "@services/analytics";
import { DELIVERY_PAGE_EVENTS } from "../../constants/analyticsEventsCT";

import Button from "../../components/Button";
import color from "../../constants/color";

const PATH = "screens/DeliveryPage";

const DeliveryPage = ({ navigation }) => {
  const dispatch = useDispatch();
  const [orders, setOrders] = useState([{}]);
  const [currentCount, setCurrentCount] = useState(0);
  const [count, setCount] = useState(0);
  const [fetchComplete, setFetchComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [undeliveredReasons, setUndeliveredReasons] = useState([]);
  const [undeliveredReason, setUndeliveredReason] = useState(null);

  const [confirmationModal, setConfirmationModal] = useState(false);
  const [notDeliveredModal, setNotDeliveredModal] = useState(false);
  const [name, setName] = useState("");
  const [refreshData, setRefreshData] = useState(false);
  const [confirmationSuccess, setConfirmationSuccess] = useState(false);
  const [payload, setPayload] = useState({});
  const [deliverySuccessPayload, setDeliverySuccessPayload] = useState({});
  const [deliveryFailPayload, setDeliveryFailPayload] = useState({});

  const onNotDeliver = (data, deliverfailPayload) => {
    setNotDeliveredModal(true);
    setPayload(data);
    setDeliveryFailPayload(deliverfailPayload);
  };

  const navigateToDetails = (order_id) => {
    dispatch(
      orderActions.setSelectedOrder({
        orderId: parseInt(order_id, 10),
        source: "FORWARD",
      }),
    );
    navigation.navigate("OrderDetails");
  };

  const onNotDeliverConfirm = async () => {
    try {
      const data = {
        ...payload,
        undelivered_reason: undeliveredReasons[undeliveredReason].value,
      };
      await markOrderAsNotDelivered(data);
      setRefreshData((s) => !s);
      setNotDeliveredModal(false);
      analytics.trackEvent(DELIVERY_PAGE_EVENTS.DELIVERY_FAIL, {
        order_id: deliveryFailPayload.order_id,
        reason: undeliveredReasons[undeliveredReason].value,
      });
    } catch (e) {
      setNotDeliveredModal(false);
    }
  };
  const onDeliver = (item_ids, user_name, order_id) => {
    setPayload({ order_id });
    toggleConfirmationModal();
    setName(user_name);
    setDeliverySuccessPayload({ order_id });
  };

  const onDeliverConfirm = async () => {
    try {
      const response = await markDelivery(deliverySuccessPayload.order_id);
      handleOTPFlow(
        response,
        "confirmDelivery",
        deliverySuccessPayload.order_id,
      );
    } catch (e) {
      console.log(e);
      toggleConfirmationModal();
    }
  };

  const handleOTPFlow = (response, action, id) => {
    if (response?.data.error === "OTP_VERIFICATION_FAILED") {
      navigation.navigate("OTPVerification", {
        action,
        id,
        otpLength: response?.data?.otpLength || 4,
        otpText: response?.data?.otpText || "",
      });
    } else if (response.status === 200) {
      setRefreshData((s) => !s);
      setConfirmationSuccess(true);
      analytics.trackEvent(DELIVERY_PAGE_EVENTS.DELIVERY_SUCCESS, {
        order_id: deliverySuccessPayload.order_id,
      });
    }
    toggleConfirmationModal();
  };

  const toggleConfirmationModal = () => {
    setConfirmationModal((s) => !s);
  };

  const ListFooterComponent = () => {
    return loading && currentCount < count ? (
      <View
        style={{ paddingVertical: 10, backgroundColor: "white", height: 48 }}
      >
        <ActivityIndicator size="large" color={color.primary} />
      </View>
    ) : (
      <View
        style={{ paddingVertical: 10, backgroundColor: "white", height: 48 }}
      />
    );
  };

  const loadMore = () => {
    if (currentCount < count && !loading) {
      setLoading(true);
      (async () => {
        try {
          const { data: response } = await getDeliveriesList(10, currentCount);
          setOrders(
            orders.concat(
              response.orders
                .map((order) => {
                  const newOrder = { ...order, title: order.delivered_at };
                  delete order.delivered_at;
                  return newOrder;
                })
                .sort(
                  (a, b) =>
                    new Date(b.title).getTime() - new Date(a.title).getTime(),
                )
                .reduce((acc, curr) => {
                  const position = acc.findIndex((i) => i.title === curr.title);
                  if (position >= 0) {
                    acc[position].data.push(curr);
                    return acc;
                  } else {
                    return acc.concat({ data: [curr], title: curr.title });
                  }
                }, []),
            ),
          );
          setCurrentCount(response.orders.length + currentCount);
          setPayload({});
          setLoading(false);
        } catch (e) {
          console.log(e);
          setLoading(false);
        }
      })();
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: response } = await getDeliveriesList(50, 0);
        setOrders(
          response.orders
            .map((order) => {
              const newOrder = { ...order, title: order.delivered_at };
              delete order.delivered_at;
              return newOrder;
            })
            .sort(
              (a, b) =>
                new Date(b.title).getTime() - new Date(a.title).getTime(),
            )
            .reduce((acc, curr) => {
              const position = acc.findIndex((i) => i.title === curr.title);
              if (position >= 0) {
                acc[position].data.push(curr);
                return acc;
              } else {
                return acc.concat({ data: [curr], title: curr.title });
              }
            }, []),
        );
        setLoading(false);
        setCount(response.count);
        setCurrentCount(response.orders.length);
        setPayload({});
        setUndeliveredReasons(response.undeliveredReasons);
        setFetchComplete(true);
      } catch (e) {
        console.log(e);
        setFetchComplete(true);
      }
    })();
  }, [refreshData]);

  return (
    <View style={{ flex: 1 }}>
      <View
        style={{
          flexDirection: "row",
          paddingVertical: 15,
          backgroundColor: "white",
          borderBottomColor: "rgba(98,98,98,0.1)",
          borderBottomWidth: 1,
        }}
      >
        <TouchableOpacity
          testID="dp-back-button"
          onPress={() => {
            navigation.goBack();
          }}
          style={{
            alignItems: "center",
            paddingHorizontal: 10,
            paddingRight: 30,
          }}
        >
          <BackArrow />
        </TouchableOpacity>
        <Text style={{ fontWeight: "bold", fontSize: 20 }}>
          {t(`${PATH}/markDelivery`)}
        </Text>
      </View>

      {count ? (
        <SectionList
          sections={orders}
          keyExtractor={(item, index) => item + index}
          renderItem={({ item }) => (
            <DeliveryCard
              {...item}
              {...{
                onDeliver,
                navigateToDetails,
                onNotDeliver,
                undeliveredReasons,
              }}
            />
          )}
          contentContainerStyle={{}}
          renderSectionHeader={({ section: { title } }) => (
            <Text style={styles.header}>{title.replace(",", "")}</Text>
          )}
          onEndReached={loadMore}
          ListFooterComponent={ListFooterComponent}
        />
      ) : (
        <View
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          {fetchComplete ? (
            <>
              <NoOrdersGraphic />
              <Text
                fontType="content1"
                style={{
                  color: color.lightBlack,
                  textAlign: "center",
                  paddingTop: 20,
                }}
              >
                {t(`${PATH}/noDeliveries`)}
              </Text>
            </>
          ) : (
            <ActivityIndicator size="large" color={color.primary} />
          )}
        </View>
      )}
      <Modal
        visible={notDeliveredModal}
        transparent
        onRequestClose={() => {
          setNotDeliveredModal(false);
        }}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.6)",
            justifyContent: "flex-end",
          }}
        >
          <View
            style={{
              backgroundColor: "white",
              alignSelf: "center",
              padding: 24,
              borderTopRightRadius: 20,
              borderTopLeftRadius: 20,
              width: "100%",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 15,
              }}
            >
              <Text style={{ fontWeight: "bold", fontSize: 16 }}>
                {t(`${PATH}/reasonForNotDelivering`)}
              </Text>
              <TouchableOpacity
                testID="dp-close-button"
                onPress={() => {
                  setNotDeliveredModal(false);
                  setUndeliveredReason(null);
                }}
              >
                <CloseIcon />
              </TouchableOpacity>
            </View>

            <View>
              {undeliveredReasons.map((reason, index) => (
                <TouchableOpacity
                  testID="dp-reason-button"
                  key={`${reason.value}`}
                  onPress={() => {
                    setUndeliveredReason(index);
                  }}
                  style={{
                    justifyContent: "flex-start",
                    flexDirection: "row",
                    paddingVertical: 15,
                  }}
                >
                  <View
                    style={{
                      height: 22,
                      width: 22,
                      borderWidth: 2,
                      borderColor:
                        index === undeliveredReason ? color.primary : "black",
                      borderRadius: 100,
                      justifyContent: "center",
                      alignItems: "center",
                      marginRight: 15,
                    }}
                  >
                    <View
                      style={{
                        opacity: index === undeliveredReason ? 1 : 0,
                        borderRadius: 100,
                        backgroundColor:
                          index === undeliveredReason ? color.primary : "black",
                        height: 10,
                        width: 10,
                      }}
                    />
                  </View>
                  <Text style={{ color: color.lightBlack }}>
                    {reason.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <Button
              btnText={t(`${PATH}/submit`)}
              onPress={onNotDeliverConfirm}
              borderRadius={10}
              buttonStyle={{ width: "100%", marginTop: 5 }}
              disabled={undeliveredReason === null}
            />
          </View>
        </View>
      </Modal>
      <Modal
        visible={confirmationModal}
        transparent
        onRequestClose={() => {
          toggleConfirmationModal();
          setConfirmationSuccess(false);
        }}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.6)",
            justifyContent: "center",
          }}
        >
          {!confirmationSuccess ? (
            <View
              style={{
                backgroundColor: "white",
                alignSelf: "center",
                padding: 24,
                borderRadius: 6,
                marginHorizontal: 15,
              }}
            >
              <Text style={{ color: color.lightBlack }}>
                {t(`${PATH}/doYouWantToMarkDeliver`, { name })}
              </Text>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  marginTop: 10,
                }}
              >
                <TouchableOpacity
                  testID="dp-no-button"
                  style={{ paddingHorizontal: 15, paddingTop: 10 }}
                  onPress={toggleConfirmationModal}
                >
                  <Text style={{ color: color.lightBlack }}>
                    {t(`${PATH}/no`)}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  testID="dp-yes-button"
                  onPress={onDeliverConfirm}
                  style={{ paddingHorizontal: 15, paddingTop: 10 }}
                >
                  <Text style={{ color: color.primary }}>
                    {t(`${PATH}/yes`)}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <View
              style={{
                backgroundColor: "white",
                alignSelf: "center",
                padding: 24,
                borderRadius: 6,
                justifyContent: "center",
                alignItems: "center",
                marginHorizontal: 20,
                position: "relative",
              }}
            >
              <TouchableOpacity
                testID="dp-close-2-button"
                onPress={() => {
                  toggleConfirmationModal();
                  setConfirmationSuccess(false);
                }}
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  padding: 15,
                }}
              >
                <CloseIcon />
              </TouchableOpacity>
              <CheckMultiLayer style={{ marginTop: 60 }} />
              <Text
                style={{ paddingTop: 40, fontWeight: "bold", fontSize: 20 }}
              >
                Success!
              </Text>
              <Text
                style={{
                  color: color.lightBlack,
                  marginHorizontal: 48,
                  textAlign: "center",
                  marginTop: 10,
                  marginBottom: 60,
                }}
              >
                {t(`${PATH}/deliverySuccessful`, { name })}
              </Text>
            </View>
          )}
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    fontWeight: "bold",
    fontSize: 20,
    backgroundColor: "white",
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0,0,0,0.1)",
    marginTop: 10,
  },
});

export default DeliveryPage;
